import React from 'react'
import PropTypes from 'prop-types'
import { graphql } from 'gatsby'
import styled from 'styled-components'
import kebabCase from 'lodash/kebabCase'

import useAttachPageEventHandlers from 'hooks/useAttachPageEventHandlers'
import useAnalytics from 'lib/analytics/useAnalytics'
import {
  EVENT_OUTBOUND_LINK_CLICKED,
  EVENT_MAILTO_LINK_CLICKED,
} from 'lib/analytics/events'

import HelpSummary from 'components/summaryCards/HelpSummary'
import Paper from 'components/Paper'
import Layout from 'components/page/layouts/GatsbyLayout'
import Helmet from 'components/page/Helmet'
import Article, { MAX_WIDTH } from 'components/Article'
import ContactAnvil from 'components/Signup/ContactAnvil'

import OtherPostsSection from 'components/page/Section/OtherPostsSection'
import Content from 'components/blog/Content'
import TitleContainer from 'components/Article/TitleContainer'
import ArrowLink from 'components/ArrowLink'
import ContactSupportForm from 'components/EmbedForm/ContactSupportForm'

import renderNavbar from 'helpers/renderNavbar'
import { getPagePath } from 'helpers/path'
import { SCROLL_NAME_CONTACT_SUPPORT } from 'helpers'
import { getOtherFormsTitle } from 'lib/htmlAst'
import { normalizeHelpPost } from 'lib/content'

import { HELP_SLUG } from 'consts'

// TODO: CANDYCODE migrate to next.js

const StyledPaperContainer = styled(Paper.Container)`
  margin-bottom: 36px;
  padding-top: 0px;
`

const StyledTitleContainer = styled(TitleContainer)`
  margin: 0 auto;
`

const StyledContent = styled(Content)`
  &:before {
    margin-top: 16px;
  }

  ol li,
  ul li {
    margin-top: 8px;
    margin-bottom: 8px;
  }
`

export default function Template ({
  location: { pathname },
  data: {
    article,
    relatedArticles: { nodes: relatedNodes },
  },
  pageContext: {
    selectedTag,
    canonicalTag,
    newHtmlAst,
    relatedNodesCurrentTags,
  },
}) {
  const mailToClickHandler = useAnalytics({
    eventName: EVENT_MAILTO_LINK_CLICKED,
  })
  const outboundLinkClickHandler = useAnalytics({
    eventName: EVENT_OUTBOUND_LINK_CLICKED,
  })

  useAttachPageEventHandlers({
    selectors: ['a[href^="http"]', 'a[href^="https"]'],
    event: 'click',
    eventHandler: (e) => {
      outboundLinkClickHandler({
        click_url: e?.target?.href,
        action: 'Clicked',
        link_text: e.target.innerText,
      })
    },
  })
  useAttachPageEventHandlers({
    selectors: ['a[href^="mailto:"]'],
    event: 'click',
    eventHandler: (e) => {
      mailToClickHandler({
        click_url: e?.target?.href,
        action: 'Clicked',
        link_text: e.target.innerText,
      })
    },
  })

  // add step count to section headers
  const headers = newHtmlAst.children.filter(
    (htmlNode) =>
      htmlNode.tagName === 'h2' ||
      (htmlNode.tagName === 'h3' &&
        htmlNode.properties.id !== 'related-articles')
  )

  // reset when we see an eyebrow header (h3 that isn't related articles)
  // give prop of step to each h2
  let count = 1
  headers.forEach((header) => {
    if (header.tagName === 'h2') {
      header.properties.step = count
      count++
    } else {
      count = 1
    }
  })

  const { title, summary, tagline, tags, content, articleWidth, slug } =
    normalizeHelpPost(article, newHtmlAst)

  const relatedArticles = relatedNodes.map((node) => {
    node.currentTag = relatedNodesCurrentTags.find(
      (relatedNode) => relatedNode.id === node.id
    )?.currentTag

    return normalizeHelpPost(node, null, { indexPage: true })
  })

  return (
    <Layout>
      <Helmet
        title={title}
        meta={{
          description: summary.plainText,
          // keywords, // TODO: enable keywords on help articles
          socialTagline: title,
        }}
        pathname={pathname}
        canonicalPathname={getPagePath({
          prefixSlug: HELP_SLUG,
          tagSlug: kebabCase(canonicalTag),
          pageSlug: slug,
        })}
      />
      {renderNavbar()}
      <StyledPaperContainer padded>
        <StyledTitleContainer
          maxWidth={articleWidth || MAX_WIDTH}
          title={title}
          tags={tags}
          summary={summary}
          tagline={tagline}
          selectedTag={selectedTag}
          link={{
            to: `/${HELP_SLUG}/`,
            text: 'Back to Help Center',
          }}
        />

        <Article maxWidth={articleWidth || MAX_WIDTH}>
          <StyledContent>
            {content}
            <ArrowLink
              to={`/${HELP_SLUG}/`}
              direction="left"
              style={{ marginBottom: '64px', marginTop: '40px' }}
            >
              Back to Help Center
            </ArrowLink>
          </StyledContent>
        </Article>
      </StyledPaperContainer>
      <OtherPostsSection
        title={getOtherFormsTitle(newHtmlAst, 'related-articles')}
        posts={relatedArticles}
        baseSlug={HELP_SLUG}
        cardComponent={HelpSummary}
        tag={selectedTag}
      />
      <ContactAnvil
        title="Contact support"
        description="We know dealing with tech support can be painful. It's our goal to help you as quickly and efficiently as possible. Once we get your message, we will email you back as soon as we can."
        scrollName={SCROLL_NAME_CONTACT_SUPPORT}
        hideSignup
        renderEmbedForm={() => <ContactSupportForm />}
      />
    </Layout>
  )
}

// TODO: audit these
Template.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  data: PropTypes.shape({
    article: PropTypes.object.isRequired,
    relatedArticles: PropTypes.shape({
      nodes: PropTypes.array,
    }),
  }),
  pageContext: PropTypes.shape({
    selectedTag: PropTypes.string.isRequired,
    canonicalTag: PropTypes.bool.isRequired,
    newHtmlAst: PropTypes.object,
    relatedNodesCurrentTags: PropTypes.object,
  }),
}

export const pageQuery = graphql`
  query HelpPostByPath($id: String, $relatedNodesIds: [String]) {
    article: markdownRemark(id: { eq: $id }) {
      frontmatter {
        slug
        title
        tags
        tagline
        summary
        articleWidth
      }
      id
      internal {
        type
      }
    }
    relatedArticles: allMarkdownRemark(
      limit: 2000
      filter: { id: { in: $relatedNodesIds } }
    ) {
      nodes {
        id
        frontmatter {
          slug
          title
          tags
          tagline
          summary
          articleWidth
        }
        internal {
          type
        }
      }
    }
  }
`
